import { useCallback, useEffect, useRef, useState } from 'react';
import { revealSpoiler, type Submission } from '@engineer-os/engine';
import { api } from '../api';
import { missionCatalog } from '../engine/runtime';
import { checkpointVerdict, type CheckpointVerdict } from '../lib/checkpoint';
import { flushEditor } from '../lib/editor-flush';
import { getStrings } from '../lib/strings';
import { verificationStatus, submitVerification, verificationAttempt, type VerificationAllowance } from '../lib/verification';
import type { GradeResult, PublicMission } from '../types';

export type CheckpointProblem =
  | 'signIn' | 'quota' | 'busyElsewhere' | 'submission' | 'unavailable' | 'paused';
export type AssessmentStatus = 'idle' | 'checking' | 'waiting' | 'reconnecting' | 'limited';

export interface CheckpointCheck {
  verdict: CheckpointVerdict | null;
  /** Only local work holds the Check button. Remote work has its own state. */
  checking: boolean;
  assessmentStatus: AssessmentStatus;
  /** A submitted snapshot's result must not overwrite newer progress checks. */
  earlierAssessment: CheckpointVerdict | null;
  stale: boolean;
  output: string;
  problem: CheckpointProblem | null;
  limits: VerificationAllowance | null;
  passed: boolean;
  reflection: string | null;
  /**
   * Presses of "Check my work" that ran, on this checkpoint, this page load.
   * The session's own count never sees them — a checkpoint is not graded by
   * the session — so the clear overlay read "It took 0 checks" off it.
   */
  checks: number;
  check(): Promise<GradeResult | null>;
  retryAssessment(): void;
  forget(): void;
}

interface PendingAssessment {
  controller: AbortController;
  id?: string;
  /** Retained until upload acceptance is known; retries use these same bytes. */
  submission?: Submission;
  revision: number | null;
  view: number;
  startedAt: number;
  delay: number;
  inFlight: boolean;
  quotaUntil?: number;
  timer?: ReturnType<typeof setTimeout>;
  resolve(result: GradeResult | null): void;
}

function problemFor(status: number | undefined, limits: VerificationAllowance | undefined): CheckpointProblem {
  if (status === 401) return 'signIn';
  if (status === 429) return limits?.deploymentPaused ? 'paused' : 'quota';
  if (status === 409) return 'busyElsewhere';
  if (status === 400 || status === 413) return 'submission';
  return 'unavailable';
}

/**
 * Checks per checkpoint, kept for the page rather than the mount: leaving a
 * level and coming back keeps its session, so it keeps its count too, the way
 * a level's does.
 */
const checksRun = new Map<string, number>();

/** Public checks and assessment polling run independently. A local check never
 * creates a second pending assessment, and a late assessment can award the
 * badge for its snapshot without replacing feedback on newer work.
 */
export function useCheckpoint(mission: PublicMission, sessionId: string | null, userId: string | null,
  enabled: boolean): CheckpointCheck {
  const [verdict, setVerdict] = useState<CheckpointVerdict | null>(null);
  const [checking, setChecking] = useState(false);
  const [assessmentStatus, setAssessmentStatus] = useState<AssessmentStatus>('idle');
  const [earlierAssessment, setEarlierAssessment] = useState<CheckpointVerdict | null>(null);
  const [stale, setStale] = useState(false);
  const [output, setOutput] = useState('');
  const [problem, setProblem] = useState<CheckpointProblem | null>(null);
  const [limits, setLimits] = useState<VerificationAllowance | null>(null);
  const [passed, setPassed] = useState(false);
  const [reflection, setReflection] = useState<string | null>(null);
  const [checks, setChecks] = useState(() => checksRun.get(mission.id) ?? 0);
  const local = useRef<AbortController | null>(null);
  const statusRequest = useRef<AbortController | null>(null);
  const remote = useRef<PendingAssessment | null>(null);
  const checkedRevision = useRef<number | null>(null);
  const view = useRef(0);
  const live = useRef(mission);
  live.current = mission;
  const follow = useRef<(job: PendingAssessment) => Promise<void>>(async () => {});

  const cancelWork = useCallback(() => {
    local.current?.abort();
    local.current = null;
    statusRequest.current?.abort();
    statusRequest.current = null;
    const job = remote.current;
    remote.current = null;
    if (job) {
      job.controller.abort();
      clearTimeout(job.timer);
      job.resolve(null);
    }
    checkedRevision.current = null;
    view.current++;
  }, []);

  const forget = useCallback(() => {
    cancelWork();
    setChecking(false);
    setAssessmentStatus('idle');
    setEarlierAssessment(null);
    setStale(false);
    setOutput('');
    setProblem(null);
    setVerdict(null);
  }, [cancelWork]);

  follow.current = async job => {
    if (remote.current !== job || job.inFlight || job.controller.signal.aborted) return;
    clearTimeout(job.timer);
    if (job.quotaUntil && job.quotaUntil > Date.now()) {
      job.timer = setTimeout(() => { void follow.current(job); }, Math.min(2_147_483_647, job.quotaUntil - Date.now()));
      return;
    }
    job.inFlight = true;
    const active = () => remote.current === job && !job.controller.signal.aborted;
    const schedule = () => {
      job.timer = setTimeout(() => { void follow.current(job); }, job.delay);
    };
    try {
      if (job.quotaUntil) {
        job.quotaUntil = undefined;
        job.startedAt = Date.now();
        job.delay = 2000;
        setProblem(null);
        setAssessmentStatus('checking');
      }
      const result = job.submission
        ? await submitVerification(job.submission, job.controller.signal, {
          attemptId: job.id,
          onReserved: id => { if (active()) job.id = id; },
        })
        : await verificationAttempt(job.id!, job.controller.signal);
      if (!active()) return;
      if (result.limits) setLimits(result.limits);
      if (result.resumed) job.revision = null;
      if (result.state === 'queued' || result.state === 'running') {
        job.id = result.attemptId ?? job.id;
        job.submission = undefined;
        job.delay = Math.min(10_000, job.delay * 1.5);
        setAssessmentStatus(Date.now() - job.startedAt > 125_000 ? 'waiting' : 'checking');
        schedule();
        return;
      }
      if (result.state !== 'passed' && result.state !== 'failed') {
        remote.current = null;
        setAssessmentStatus('idle');
        setProblem('unavailable');
        job.resolve(null);
        return;
      }
      const next = checkpointVerdict(live.current, { state: result.state,
        failed: result.failed, skipped: result.skipped, feedback: result.feedback });
      // A check started after submission owns the main objectives, even if
      // it is still running. Keep the earlier assessment available separately.
      const currentRevision = sessionId ? await api.checkpointRevision(sessionId).catch(() => null) : null;
      if (!active()) return;
      if (view.current === job.view) {
        checkedRevision.current = job.revision;
        setVerdict(next);
        setStale(job.revision === null || currentRevision !== job.revision);
      } else {
        setEarlierAssessment(next);
      }
      if (result.state === 'passed') setPassed(true);
      remote.current = null;
      setAssessmentStatus('idle');
      setProblem(null);
      job.resolve(next.result);
    } catch (error) {
      if (!active()) return;
      const { status, body } = error as { status?: number; body?: { limits?: VerificationAllowance } };
      if (body?.limits) setLimits(body.limits);
      if (!status || status >= 500) {
        // Every request times out. Preserve the job and snapshot while local
        // checking stays available, and reconnect with bounded backoff.
        setAssessmentStatus('reconnecting');
        job.delay = Math.min(30_000, job.delay * 2);
        schedule();
      } else if (status === 429 && !job.id && !body?.limits?.deploymentPaused
        && body?.limits?.nextEligibleAt && body.limits.nextEligibleAt > Date.now()) {
        // Retain the clicked snapshot, never silently export later edits.
        // Another Check replaces this unadmitted snapshot with current work.
        job.quotaUntil = body.limits.nextEligibleAt;
        job.delay = Math.min(2_147_483_647, Math.max(1000, job.quotaUntil - Date.now()));
        setProblem('quota');
        setAssessmentStatus('limited');
        schedule();
      } else {
        setProblem(problemFor(status, body?.limits));
        if (job.id && (status === 401 || status === 403)) {
          setAssessmentStatus('reconnecting');
        } else {
          remote.current = null;
          setAssessmentStatus('idle');
          job.resolve(null);
        }
      }
    } finally {
      job.inFlight = false;
    }
  };

  const retryAssessment = useCallback(() => {
    const job = remote.current;
    if (!job || job.inFlight) return;
    if (job.quotaUntil && job.quotaUntil > Date.now()) return;
    job.delay = 2000;
    setProblem(null);
    void follow.current(job);
  }, []);

  useEffect(() => {
    forget();
    setLimits(null);
    setPassed(false);
    if (!enabled) return cancelWork;
    const controller = new AbortController();
    statusRequest.current = controller;
    const initialView = view.current;
    void verificationStatus(controller.signal).then(status => {
      if (controller.signal.aborted) return;
      setLimits(status.limits ?? null);
      if (status.completions.some(c => c.missionId === mission.id)) setPassed(true);
      const pending = status.pending?.find(p => p.missionId === mission.id);
      if (pending && !remote.current) {
        const job: PendingAssessment = { controller: new AbortController(), id: pending.id,
          revision: null, view: initialView, startedAt: Date.now(), delay: 2000, inFlight: false, resolve: () => {} };
        remote.current = job;
        setAssessmentStatus('checking');
        void follow.current(job);
      }
    }).catch(() => { /* Local checking remains available without a connection. */ });
    return cancelWork;
  }, [mission.id, sessionId, userId, enabled, forget, cancelWork]);

  useEffect(() => {
    window.addEventListener('online', retryAssessment);
    return () => window.removeEventListener('online', retryAssessment);
  }, [retryAssessment]);

  useEffect(() => {
    if (!enabled || !sessionId) return;
    let alive = true;
    const timer = setInterval(() => {
      const revision = checkedRevision.current;
      if (revision === null) return;
      void api.checkpointRevision(sessionId).then(now => {
        if (alive && checkedRevision.current === revision) setStale(now !== revision);
      }).catch(() => {});
    }, 1000);
    return () => { alive = false; clearInterval(timer); };
  }, [enabled, sessionId]);

  useEffect(() => {
    setReflection(null);
    if (!passed) return;
    let alive = true;
    void missionCatalog().then(catalogue => {
      const full = catalogue.get(mission.id);
      if (alive && full) setReflection(revealSpoiler(full.reflection, full.id, 0));
    }).catch(() => {});
    return () => { alive = false; };
  }, [passed, mission]);

  const check = useCallback(async (): Promise<GradeResult | null> => {
    if (!enabled || !sessionId || local.current) return null;
    const waiting = remote.current;
    if (waiting?.quotaUntil && !waiting.inFlight) {
      remote.current = null;
      waiting.controller.abort();
      clearTimeout(waiting.timer);
      waiting.resolve(null);
      setAssessmentStatus('idle');
    }
    const controller = new AbortController();
    local.current = controller;
    const currentView = ++view.current;
    const alreadyAssessing = remote.current !== null;
    setChecking(true);
    if (!alreadyAssessing) setProblem(null);
    try {
      await flushEditor(sessionId);
      if (controller.signal.aborted) return null;
      setStale(false);
      setOutput('');
      const revision = await api.checkpointRevision(sessionId);
      const practice = await api.checkpointPractice(sessionId);
      if (controller.signal.aborted) return null;
      const ran = (checksRun.get(live.current.id) ?? 0) + 1;
      checksRun.set(live.current.id, ran);
      setChecks(ran);
      const s = getStrings().checkpoint;
      const progress: GradeResult = { ...practice.grade, objectives: practice.grade.objectives.map(o => ({
        ...o, title: live.current.objectives.find(v => v.id === o.id)?.title ?? o.title,
        ...(!o.passed ? { message: o.id === 'tidy' ? s.dirty : o.id === 'on-branch' ? s.branchFailed : s.suiteFailed } : {}),
      })) };
      checkedRevision.current = revision;
      setVerdict({ grade: progress, result: progress, gates: [] });
      setOutput(practice.output);
      const current = await api.checkpointRevision(sessionId);
      if (controller.signal.aborted) return null;
      setStale(current !== revision);
      if (!practice.ready || current !== revision || alreadyAssessing || remote.current) return null;
      if (!userId) { setProblem('signIn'); return null; }
      const submission = await api.exportSubmission(sessionId);
      if (controller.signal.aborted) return null;
      if (await api.checkpointRevision(sessionId) !== revision) { setStale(true); return null; }
      if (controller.signal.aborted || remote.current) return null;
      // A delayed initial lookup must not restore an obsolete pending job
      // after this submission has already completed.
      statusRequest.current?.abort();
      setEarlierAssessment(null);
      // Return the eventual grade to Play's completion handler, but do not
      // await it here: finally releases the local Check button immediately.
      return new Promise(resolve => {
        const job: PendingAssessment = { controller: new AbortController(), submission,
          revision, view: currentView, startedAt: Date.now(), delay: 2000, inFlight: false, resolve };
        remote.current = job;
        setAssessmentStatus('checking');
        void follow.current(job);
      });
    } catch (error) {
      if (!controller.signal.aborted) {
        const { status } = error as { status?: number };
        setProblem(problemFor(status, undefined));
      }
      return null;
    } finally {
      if (local.current === controller) {
        local.current = null;
        setChecking(false);
      }
    }
  }, [enabled, sessionId, userId]);

  return { verdict, checking, assessmentStatus, earlierAssessment, stale, output, problem, limits, passed,
    reflection, checks, check, retryAssessment, forget };
}
